import {
  json_parse,
  type NetworkType,
  OnchainSDK,
} from "@gearbox-protocol/sdk/onchain";
import { AccountsPlugin } from "@gearbox-protocol/sdk/plugins/accounts";
import { STATE_CACHE_URL_BASE } from "api";
import type { SdkStateLanguage } from "utils";
import type { Address } from "viem";
import { create } from "zustand";

export interface SDKStore {
  networkType: NetworkType;
  language: SdkStateLanguage;
  sdk: OnchainSDK | undefined;
  curator: Address | undefined;
  market: Address | undefined;
  creditManager: Address | undefined;
  loading: boolean;
  error: Error | undefined;
  fromCache: boolean;
}

export interface SDKStoreMutators {
  setNetworkType: (n: NetworkType) => void;
  setLanguage: (l: SdkStateLanguage) => void;
  setCurator: (c: Address | undefined) => void;
  setMarket: (m: Address | undefined) => void;
  setCreditManager: (c: Address | undefined) => void;
}

export const useSDKStore = create<SDKStore & SDKStoreMutators>((set, get) => {
  const loadSDK = async (networkType: NetworkType) => {
    set(() => ({
      networkType,
      sdk: undefined,
      error: undefined,
      loading: true,
      curator: undefined,
      market: undefined,
      creditManager: undefined,
      fromCache: false,
    }));
    try {
      const resp = await fetch(`${STATE_CACHE_URL_BASE}/${networkType}.json`);
      const body = await resp.text();
      const rawState = json_parse(body);
      const sdk = new OnchainSDK(
        networkType,
        {
          rpcURLs: ["http://null.local"],
        },
        {
          plugins: {
            accounts: new AccountsPlugin({ includeZeroDebt: true }, true),
          },
        },
      );
      sdk.hydrate(rawState);
      set(() => ({ sdk, fromCache: true }));
    } catch (e) {
      set(() => ({ error: e as Error }));
    } finally {
      set(() => ({ loading: false }));
    }
  };

  return {
    networkType: "Mainnet",
    language: "yaml",
    sdk: undefined,
    curator: undefined,
    market: undefined,
    creditManager: undefined,
    loading: false,
    error: undefined,
    fromCache: false,
    preferCached: true,

    setLanguage: (l: SdkStateLanguage) => set(() => ({ language: l })),
    setNetworkType: async (n: NetworkType) => {
      const { sdk, networkType, loading } = get();
      if ((!!sdk || loading) && networkType === n) {
        return;
      }
      await loadSDK(n);
    },
    setCurator: (c: Address | undefined) =>
      set(() => ({ curator: c, market: undefined, creditManager: undefined })),
    setMarket: (m: Address | undefined) =>
      set(() => ({ market: m, creditManager: undefined })),
    setCreditManager: (c: Address | undefined) =>
      set(() => ({ creditManager: c })),
  };
});
